import React from 'react';
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { IBiePlanningContext } from '../planningcore';
import { ComboRow } from '../core/coremodels';
import { ActivityPlanningScopeCode } from './activityplanning.models';

type Props = {
  planningContext: IBiePlanningContext | null;
  costCenterCode: string;
  axisCode: string;
  globalObjectiveCode: string;
  specificObjectiveCode: string;
  scope: ActivityPlanningScopeCode;
  canplan: boolean;

  costCenters: ComboRow[];
  axes: ComboRow[];
  globalObjectives: ComboRow[];
  specificObjectives: ComboRow[];

  onChangePlanningCycle: () => void;
  onCostCenterChange: (code: string) => void;
  onAxisChange: (code: string) => void;
  onGlobalObjectiveChange: (code: string) => void;
  onSpecificObjectiveChange: (code: string) => void;
  onRefresh: () => void;
  onClear: () => void;
  loading?: boolean;
};

const label = (x: ComboRow) => `${x.shc} - ${x.nam}`;

class ActivityPlanningFilterBar extends React.Component<Props> {
  render() {
    const p = this.props;
    const planningCycleName = p.planningContext?.pcn ?? '';
    const budgetPeriodName = p.planningContext?.bpn ?? '';

    return (
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <div>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Planning Context</Typography>
            <Typography variant="caption" color="text.secondary">
              Planning Cycle and Cost Centre define the governed workspace. Axis and OG are read scopes; planning is enabled at Specific Objective (OS).
            </Typography>
          </div>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color={p.canplan ? 'success.main' : 'text.secondary'}>
              {p.canplan ? 'PLANNING MODE' : `${p.scope.replace('_', ' ')} · READ`}
            </Typography>
            <Button variant="outlined" onClick={p.onClear} disabled={p.loading}>Clear Scope</Button>
            <Button variant="contained" onClick={p.onRefresh} disabled={p.loading}>Refresh</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField size="small" label="Planning Cycle" value={planningCycleName} disabled fullWidth />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField size="small" label="Budget Period" value={budgetPeriodName} disabled fullWidth />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField select size="small" label="Cost Centre" value={p.costCenterCode}
              onChange={(e) => p.onCostCenterChange(e.target.value)} fullWidth>
              <MenuItem value="">Select Cost Centre</MenuItem>
              {p.costCenters.map((x) => <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField select size="small" label="Axis" value={p.axisCode}
              onChange={(e) => p.onAxisChange(e.target.value)} fullWidth>
              <MenuItem value="">Select Axis</MenuItem>
              {p.axes.map((x) => <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField select size="small" label="Global Objective (OG)" value={p.globalObjectiveCode}
              onChange={(e) => p.onGlobalObjectiveChange(e.target.value)} disabled={!p.axisCode} fullWidth>
              <MenuItem value="">Select OG</MenuItem>
              {p.globalObjectives.map((x) => <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField select size="small" label="Specific Objective (OS)" value={p.specificObjectiveCode}
              onChange={(e) => p.onSpecificObjectiveChange(e.target.value)} disabled={!p.globalObjectiveCode} fullWidth>
              <MenuItem value="">Select OS</MenuItem>
              {p.specificObjectives.map((x) => <MenuItem key={x.shc} value={x.shc}>{label(x)}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

export default ActivityPlanningFilterBar;
